import { ConfigsContextValue } from '@apply/providers/ConfigsProvider';

export type ApplicationStatus =
    | 'INCOMPLETE'
    | 'PENDING'
    | 'IN_REVIEW'
    | 'APPROVED'
    | 'DENIED'
    | 'SELECTED_LOAN_OPTION'
    | 'FAILED_VALIDATION'
    | 'EXPIRED'
    | 'IGNORED'
    | 'SIGNED'
    | 'IMPORTING'
    | 'IMPORTED'
    | 'BLOCKED';

export interface LoanApplicationIncome {
    id: number | null;
    primary: boolean | null;
    source: string | null;
    rate_type: string | null;
    next_pay_date: string | null;
    last_pay_date: string | null;
    employer_name: string | null;
    employer_phone: string | null;
    amount: number | null;
    frequency: string | null;
}

export interface Agreements {
    agreed_terms_of_use: boolean | null;
    agreed_privacy_policy: boolean | null;
    agreed_e_consent: boolean | null;
    agreed_sms_terms: boolean | null;
}

export interface ApplicationFields {
    id: string | null;
    place_left: string | null;
    application_status: {
        status: ApplicationStatus | null;
        metadata: {
            persona_inquiry_id: string;
        } | null;
    };
    amount_applied: number | null;
    loan_payment_amount_initial: number | null;
    loan_payment_frequency_initial: string | null;
    frequency_applied: string | null;
    loan_duration_months: number | null;
    number_of_payments_applied: number | null;
    loan_purpose: string | null;
    email: string | null;
    first_name: string | null;
    last_name: string | null;
    borrower_ssn_masked: string | null;
    date_of_birth: string | null;
    military_flag: boolean | null;
    additional_income_flag: boolean | null;
    loan_application_incomes: LoanApplicationIncome[];
    bankruptcy_status_flag: boolean | null;
    number_of_bankruptcies: number | null;
    used_a_payday_loan_flag: boolean | null;
    number_of_payday_loans: number | null;
    moving_date: string | null;
    housing_status: string | null;
    monthly_housing_expense: number | null;
    evicted_status_flag: boolean | null;
    number_of_evictions: number | null;
    address_1: string | null;
    address_2: string | null;
    city: string | null;
    state: string | null;
    zip: string | null;
    phone_number: string | null;
    phone_number_type: string | null;
    borrower_secondary_phone: string | null;
    bank_account_open_time: string | null;
    bank_account_type: string | null;
    bank_account_number_masked: string | null;
    bank_routing_number_masked: string | null;
    direct_deposit: boolean | null;
    auto_debit: boolean | null;
    referral_source: string | null;
    lead_id: string | null;
}

export type Application = ApplicationFields & Agreements;

export type LocalApplication = Pick<
    ApplicationFields,
    | 'amount_applied'
    | 'loan_payment_amount_initial'
    | 'loan_payment_frequency_initial'
    | 'loan_purpose'
>;

export const USER_TYPES = {
    NEW: 'NEW',
    REPEAT: 'REPEAT',
    PREFERRED: 'PREFERRED',
} as const;

export type User = {
    aprType: keyof typeof USER_TYPES;
    flowType: keyof typeof USER_TYPES;
    applicationId: string | null;
};

export type ScreenValidatorParams = {
    application: Partial<Application>;
    user?: Partial<User>;
    configs?: Partial<ConfigsContextValue>;
    extraValidationFailed?: boolean;
};

export const STATIC_AGREEMENT = {
    PRIVACY_POLICY: 'PRIVACY_POLICY',
    TERMS_OF_USE: 'TERMS_OF_USE',
    ELECTRONIC_COMMUNICATIONS_DISCLOSURE_STATEMENT_AND_CONSENT:
        'ELECTRONIC_COMMUNICATIONS_DISCLOSURE_STATEMENT_AND_CONSENT',
    SMS_TERMS_AND_CONDITIONS: 'SMS_TERMS_AND_CONDITIONS',
} as const;

export const DYNAMIC_AGREEMENT = {
    AUTOMATED_CLEARING_HOUSE_DEBT_AUTHORIZATION_AGREEMENT:
        'AUTOMATED_CLEARING_HOUSE_DEBT_AUTHORIZATION_AGREEMENT',
    CONFIRMATION_DISCLOSURES: 'CONFIRMATION_DISCLOSURES',
    LOAN_AGREEMENT: 'LOAN_AGREEMENT',
} as const;

export type DynamicAgreement = keyof typeof DYNAMIC_AGREEMENT;

export type DynamicAgreementData = {
    title: string;
    html: string;
    version: string;
    checkboxText: string;
};
export type AllDynamicAgreementData = {
    [DYNAMIC_AGREEMENT.LOAN_AGREEMENT]: DynamicAgreementData;
    [DYNAMIC_AGREEMENT.CONFIRMATION_DISCLOSURES]: DynamicAgreementData;
    [DYNAMIC_AGREEMENT.AUTOMATED_CLEARING_HOUSE_DEBT_AUTHORIZATION_AGREEMENT]?: DynamicAgreementData;
};

export type Offer = {
    amount: number;
    payment: number;
    payment_frequency: string;
    number_of_payments: number;
    months: number;
    apr: number;
};

export type DefaultOffer = {
    borrower_type: User['aprType'];
    offer: Offer;
};

export const PVVerifyOTP = {
    phone_saved: 'phone_saved',
    bad_code: 'bad_code',
    expired_code: 'expired_code',
    blocked: 'blocked',
} as const;

export const PVSubmitPhone = {
    phone_saved: 'phone_saved',
    invalid_phone: 'invalid_phone',
    code_sent: 'code_sent',
    cooldown: 'cooldown',
    blocked: 'blocked',
} as const;

export type LeadData = {
    first_name: string;
    last_name: string;
    masked_ssn: string;
    dob: string;
    email: string;
    phone_number: string;
    address: string;
    masked_bank_account_number: string;
    bank_routing_number: string;
    monthly_income: number;
    next_payment_date: string;
    military_flag: boolean;
    city: string;
    zip: string;
    state: string;
    default_offer: Offer;
    offers: Offer[];
};

export type LeadDataSimplified = Omit<LeadData, 'default_offer' | 'offers'>;

export type CreateLeadApplicationInput = {
    lead_id: string;
    amount: number;
    apr: number;
    months: number;
    payment_frequency: string;
    payment: number;
    number_of_payments: number;
};

export type CreateLeadApplicationResponse = {
    create_lead_application: {
        success: boolean;
        error?: string;
    };
};
